import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  checkIsRoutableState,
  checkIsOfflineState,
} from '@/constants/amazonConnect';
import type { ConnectAgentStateDefinition } from '@/types';
import { useAmazonConnectAgent } from '@/hooks/useAmazonConnectAgent';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import { useAppSelector } from '@/store/hooks';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import {
  hasActiveCallLeg,
  isChatContact,
  isContactInAfterChatWorkState,
} from './contactUtils';

function getStateColor(state: string | undefined): string {
  if (!state) return 'bg-gray-400';
  if (checkIsRoutableState(state)) return 'bg-green-500';
  if (checkIsOfflineState(state)) return 'bg-gray-500';
  return 'bg-yellow-500';
}

function getStateDotColor(stateName: string): string {
  const lower = stateName.toLowerCase();
  if (lower === 'available' || lower === 'online' || lower === 'routable')
    return 'bg-green-500';
  if (lower === 'offline') return 'bg-gray-500';
  return 'bg-yellow-500';
}

export function AgentStatusDropdown() {
  const { t, i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [availableStates, setAvailableStates] = useState<
    ConnectAgentStateDefinition[]
  >([]);

  const { agent, agentState, isRoutable, isChangingState, setIsChangingState } =
    useAmazonConnectAgent();
  const { activeContacts, incomingContact } = useAmazonConnectContacts();
  const callContactId = useAppSelector(s => s.call.contactId);

  const hasActiveLeg = activeContacts.some(hasActiveCallLeg);
  const hasActiveChatSession = activeContacts.some(
    c => isChatContact(c) && hasActiveCallLeg(c)
  );
  const hasLiveChatFromStore =
    Boolean(callContactId) &&
    activeContacts.some(c => {
      try {
        return (
          isChatContact(c) &&
          c.getContactId() === callContactId &&
          !isContactInAfterChatWorkState(c)
        );
      } catch {
        return false;
      }
    });
  const isIncomingChat =
    incomingContact != null && isChatContact(incomingContact);
  /** Chat: Connect availability often stays Routable while handling chat. */
  const showChatBusy =
    hasActiveChatSession || hasLiveChatFromStore || isIncomingChat;

  const disableDropdown =
    hasActiveLeg || isIncomingChat || hasLiveChatFromStore;

  // Fetch available agent states from CCP when agent is available
  useEffect(() => {
    try {
      const states = agent?.getAgentStates() || [];
      setAvailableStates(
        states.map(s => ({
          type: s.type,
          name: i18n.exists(`components.amazonConnect.states.${s.name}`)
            ? t(`components.amazonConnect.states.${s.name}`)
            : s.name,
          agentStateARN: s.agentStateARN,
        }))
      );
    } catch {
      setAvailableStates([]);
    }
  }, [agent, i18n, t]);

  const handleSelectState = (stateOption: ConnectAgentStateDefinition) => {
    if (isChangingState) {
      setIsOpen(false);
      return;
    }
    setIsChangingState(true);
    const timeoutId = setTimeout(() => setIsChangingState(false), 5000);

    connectAdapter.setAgentStateByDefinition(stateOption, {
      success: () => {
        clearTimeout(timeoutId);
        setIsChangingState(false);
        setIsOpen(false);
      },
      failure: () => {
        clearTimeout(timeoutId);
        setIsChangingState(false);
        setIsOpen(false);
      },
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayLabel = !agent
    ? t('components.amazonConnect.loading')
    : showChatBusy
      ? t('components.amazonConnect.busy')
      : agentState;

  const statusIndicatorState = showChatBusy ? 'busy' : agentState;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => !disableDropdown && setIsOpen(prev => !prev)}
        disabled={isChangingState || disableDropdown}
        title={
          disableDropdown
            ? t(
                'components.amazonConnect.cannotChangeStatusDuringCall',
                'Cannot change status during an active call'
              )
            : undefined
        }
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed text-left min-w-[140px]"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t('common.status')}
      >
        <span
          className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStateColor(statusIndicatorState)} ${isRoutable && !showChatBusy ? 'animate-pulse' : ''}`}
          aria-hidden
        />
        <span className="flex-1 truncate text-sm font-medium text-gray-700">
          {displayLabel}
        </span>
        {isChangingState ? (
          <span
            className="w-4 h-4 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin flex-shrink-0"
            aria-hidden
          />
        ) : (
          <svg
            className={`w-4 h-4 flex-shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </button>

      {isOpen && (
        <ul
          className="absolute top-full left-0 mt-1 min-w-[200px] max-h-72 overflow-y-auto py-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
          role="listbox"
        >
          {availableStates.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">
              {agent
                ? t('components.amazonConnect.loading')
                : t('components.amazonConnect.pleaseWait')}
            </li>
          ) : (
            availableStates.map(stateOption => {
              const isCurrent =
                stateOption.name.toLowerCase() === agentState?.toLowerCase();
              return (
                <li
                  key={stateOption.agentStateARN}
                  role="option"
                  aria-selected={isCurrent}
                >
                  <button
                    type="button"
                    onClick={() => handleSelectState(stateOption)}
                    disabled={isChangingState || isCurrent}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
                      isCurrent
                        ? 'bg-primary-50 text-primary-800 font-medium'
                        : 'text-gray-700'
                    }`}
                  >
                    <span
                      className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStateDotColor(stateOption.name)}`}
                      aria-hidden
                    />
                    {stateOption.name}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
